import { GridSizeType, gridSize } from '@/shared';
import { Autocomplete, Grid, SxProps, TextField, Theme } from '@mui/material';
import { useCallback } from 'react';

export type CustomAutocompleteNoFormProps<T> = {
  label?: string;
  disabled?: boolean;
  options: T[];
  valueKey: keyof T;
  actualValueKey?: keyof T;
  optionLabelForEdit?: string;
  isLoadingData?: boolean;
  onChangeValue?: (value: any) => void;
  onChangeRawValue?: (value: T) => void;
  size?: GridSizeType;
  value?: string | number;
  defaultValue?: string | number;
  variant?: 'standard' | 'outlined' | 'filled';
  sxAutocomplete?: SxProps<Theme>;
  disableClearable?: boolean;
  useContainsFilter?: boolean;
};

function CustomAutocompleteNoForm<T>({
  label,
  value,
  defaultValue,
  options,
  isLoadingData,
  optionLabelForEdit,
  valueKey,
  actualValueKey,
  onChangeValue,
  onChangeRawValue,
  size = gridSize,
  disabled = false,
  variant = 'outlined',
  sxAutocomplete,
  disableClearable = false,
  useContainsFilter = false,
}: Readonly<CustomAutocompleteNoFormProps<T>>) {

  // Manejador de cambios en la selección
  const handleChange = useCallback(
    (_event: React.SyntheticEvent, data: T | string | null) => {
      const selectedValue =
        data && typeof data === 'object'
          ? data[actualValueKey ?? valueKey]
          : data ?? '';
      onChangeValue && onChangeValue(selectedValue);
      if (typeof data === 'object') {
        onChangeRawValue && onChangeRawValue(data!);
      }
    },
    [actualValueKey, onChangeRawValue, onChangeValue, valueKey]
  );

  const containsFilter = (optionsList: T[], state: any) => {
    const text = state.inputValue.toLowerCase();
    return optionsList.filter(option =>
      String(option[valueKey]).toLowerCase().includes(text)
    );
  };

  /* const onChange = (_event: any, data: any) => {
    const selectedValue = data?.[actualValueKey ?? valueKey] ?? '';
    onChangeValue && onChangeValue(selectedValue);
    onChangeRawValue && onChangeRawValue(data);
  }; */

  return (
    <Grid item {...size}>
      <Autocomplete
        freeSolo
        loading={isLoadingData}
        options={options}
        disableClearable={disableClearable}
        {...(useContainsFilter && { filterOptions: containsFilter })}
        getOptionLabel={(option: any) =>
          option?.[valueKey] ??
          options.find(opt => opt[actualValueKey ?? valueKey] === option)?.[
          valueKey
          ] ??
          optionLabelForEdit ??
          ''
        }
        value={value as any}
        defaultValue={defaultValue as any}
        disabled={disabled}
        onChange={handleChange}
        renderInput={params => (
          <TextField
            {...params}
            autoComplete='off'
            label={label}
            variant={variant}
            disabled={disabled}
            sx={{
              ...(disabled
                ? {
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "rgba(0, 0, 0, 0.035)",
                    cursor: "not-allowed",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(239, 239, 239, 0.16)",
                      borderStyle: "dashed",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(0, 0, 0, 0.22)",
                    },
                  },
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "rgba(15, 23, 42, 0.88)",
                    color: "rgba(15, 23, 42, 0.88)",
                    fontWeight: 600,
                    cursor: "not-allowed",
                  },
                  "& .MuiInputLabel-root.Mui-disabled": {
                    color: "text.secondary",
                    fontWeight: 500,
                  },
                }
                : {
                  "& .MuiOutlinedInput-root": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#d3d3d3",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#9395ec",
                    },
                  },
                }),
              ...sxAutocomplete,
            }}
          />
        )}
      />
    </Grid>
  );
}

export default CustomAutocompleteNoForm;
